/**
 * Twitter/X API Integration
 * 
 * Provides functions for OAuth 1.0a authentication and posting media to Twitter.
 * Uses the app's Netlify functions as a backend proxy.
 */

// OAuth 1.0a endpoints
const TWITTER_AUTH_ENDPOINT = '/.netlify/functions/twitter-auth-oauth1';
const TWITTER_CALLBACK_ENDPOINT = '/.netlify/functions/twitter-callback-oauth1';
const TWITTER_POST_ENDPOINT = '/.netlify/functions/twitter-post';

// Storage keys
const TWITTER_ACCESS_TOKEN_KEY = 'twitter_access_token';
const TWITTER_ACCESS_TOKEN_SECRET_KEY = 'twitter_access_token_secret';
const TWITTER_USERNAME_KEY = 'twitter_username';
const TWITTER_USER_TIER_KEY = 'twitter_user_tier';
const TWITTER_USER_ID_KEY = 'twitter_user_id';

export type TwitterUserTier = 'basic' | 'premium';

export interface TwitterTokens {
  accessToken: string;
  accessTokenSecret?: string;
  username?: string;
  userTier?: TwitterUserTier;
  userId?: string;
}

export interface TwitterPostResult {
  success: boolean;
  tweetId?: string;
  tweetUrl?: string;
  error?: string;
}

/**
 * Check if the user is connected to Twitter
 */
export const isTwitterConnected = (): boolean => {
  const accessToken = localStorage.getItem(TWITTER_ACCESS_TOKEN_KEY);
  const accessTokenSecret = localStorage.getItem(TWITTER_ACCESS_TOKEN_SECRET_KEY);
  
  // OAuth 1.0a tokens don't expire, so just check if they exist
  return !!(accessToken && accessTokenSecret);
};

/**
 * Get stored Twitter tokens
 */
export const getTwitterTokens = (): TwitterTokens | null => {
  const accessToken = localStorage.getItem(TWITTER_ACCESS_TOKEN_KEY);
  const accessTokenSecret = localStorage.getItem(TWITTER_ACCESS_TOKEN_SECRET_KEY);
  
  if (!accessToken || !accessTokenSecret) return null;
  
  const tierStr = localStorage.getItem(TWITTER_USER_TIER_KEY);
  const userTier: TwitterUserTier = tierStr === 'premium' ? 'premium' : 'basic';
  
  return {
    accessToken,
    accessTokenSecret,
    username: localStorage.getItem(TWITTER_USERNAME_KEY) || undefined,
    userTier,
    userId: localStorage.getItem(TWITTER_USER_ID_KEY) || undefined,
  };
};

/**
 * Get connected Twitter username
 */
export const getTwitterUsername = (): string | null => {
  return localStorage.getItem(TWITTER_USERNAME_KEY);
};

/**
 * Get Twitter user tier (basic or premium)
 * Premium users (Twitter Blue/X Premium) have 25,000 character limit
 * Basic users have 280 character limit
 */
export const getTwitterUserTier = (): TwitterUserTier => {
  const tier = localStorage.getItem(TWITTER_USER_TIER_KEY);
  return (tier === 'premium') ? 'premium' : 'basic';
};

/**
 * Get Twitter character limit based on user tier
 */
export const getTwitterCharLimit = (): number => {
  const tier = getTwitterUserTier();
  return tier === 'premium' ? 25000 : 280;
};

/**
 * Store Twitter tokens and user info
 */
export const storeTwitterTokens = (tokens: {
  accessToken: string;
  accessTokenSecret?: string;
  username?: string;
  userTier?: TwitterUserTier;
  userId?: string;
}): void => {
  localStorage.setItem(TWITTER_ACCESS_TOKEN_KEY, tokens.accessToken);
  
  if (tokens.accessTokenSecret) {
    localStorage.setItem(TWITTER_ACCESS_TOKEN_SECRET_KEY, tokens.accessTokenSecret);
  }
  
  if (tokens.username) {
    localStorage.setItem(TWITTER_USERNAME_KEY, tokens.username);
  }
  
  if (tokens.userTier) {
    localStorage.setItem(TWITTER_USER_TIER_KEY, tokens.userTier);
  }
  
  if (tokens.userId) {
    localStorage.setItem(TWITTER_USER_ID_KEY, tokens.userId);
  }
};

/**
 * Disconnect Twitter (clear stored tokens)
 */
export const disconnectTwitter = (): void => {
  localStorage.removeItem(TWITTER_ACCESS_TOKEN_KEY);
  localStorage.removeItem(TWITTER_ACCESS_TOKEN_SECRET_KEY);
  localStorage.removeItem(TWITTER_USERNAME_KEY);
  localStorage.removeItem(TWITTER_USER_TIER_KEY);
  localStorage.removeItem(TWITTER_USER_ID_KEY);
};

/**
 * Initiate Twitter OAuth 1.0a flow
 * Opens a popup window for the user to authorize the app
 * Returns a promise that resolves when authorization is complete
 */
export const initiateTwitterAuth = (): Promise<TwitterTokens> => {
  return new Promise((resolve, reject) => {
    let oauthTokenSecret: string;
    let popup: Window | null = null;
    let checkClosedInterval: ReturnType<typeof setInterval> | null = null;
    let resolved = false;

    const cleanup = () => {
      if (checkClosedInterval) {
        clearInterval(checkClosedInterval);
        checkClosedInterval = null;
      }
      window.removeEventListener('message', messageHandler);
    };

    const messageHandler = async (event: MessageEvent) => {
      // Verify origin matches our site
      if (event.origin !== window.location.origin) return;

      // Handle OAuth 1.0a callback
      if (event.data?.type === 'TWITTER_AUTH_1_0A') {
        const { oauthToken, oauthVerifier } = event.data;

        try {
          // Exchange the verifier for access tokens
          const response = await fetch(TWITTER_CALLBACK_ENDPOINT, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              oauthToken,
              oauthTokenSecret,
              oauthVerifier,
            }),
          });

          const data = await response.json();

          if (!response.ok) {
            throw new Error(data.error || 'Failed to exchange authorization');
          }

          // Store tokens
          const tokens: TwitterTokens = {
            accessToken: data.accessToken,
            accessTokenSecret: data.accessTokenSecret,
            username: data.username,
            userTier: data.userTier || 'basic',
            userId: data.userId,
          };
          
          storeTwitterTokens(tokens);
          
          resolved = true;
          cleanup();

          // Notify callback page of success and close it
          if (popup && !popup.closed) {
            popup.postMessage({ type: 'TWITTER_AUTH_COMPLETE' }, window.location.origin);
            setTimeout(() => popup?.close(), 500);
          }

          resolve(tokens);
        } catch (err: any) {
          resolved = true;
          cleanup();
          if (popup && !popup.closed) {
            popup.close();
          }
          reject(new Error(err.message || 'Failed to complete Twitter authorization'));
        }
      } else if (event.data?.type === 'TWITTER_AUTH_ERROR') {
        resolved = true;
        cleanup();
        if (popup && !popup.closed) {
          popup.close();
        }
        reject(new Error(event.data.error || 'Twitter authorization failed'));
      }
    };

    // Request auth URL from our backend (OAuth 1.0a)
    fetch(TWITTER_AUTH_ENDPOINT, {
      method: 'GET',
      credentials: 'include',
    })
      .then(res => res.json())
      .then(data => {
        if (!data.authUrl) {
          reject(new Error(data.error || 'Failed to get Twitter auth URL'));
          return;
        }

        // Store the oauth token secret for later use
        oauthTokenSecret = data.oauthTokenSecret;

        // Open popup for Twitter authorization
        const width = 600;
        const height = 700;
        const left = window.screenX + (window.outerWidth - width) / 2;
        const top = window.screenY + (window.outerHeight - height) / 2;
        
        popup = window.open(
          data.authUrl,
          'twitter_auth',
          `width=${width},height=${height},left=${left},top=${top},scrollbars=yes`
        );

        if (!popup) {
          reject(new Error('Failed to open popup. Please allow popups for this site.'));
          return;
        }

        // Listen for message from callback page
        window.addEventListener('message', messageHandler);

        // Check if popup was closed without completing auth
        checkClosedInterval = setInterval(() => {
          if (popup?.closed && !resolved) {
            cleanup();
            reject(new Error('Authorization cancelled'));
          }
        }, 1000);
      })
      .catch(err => {
        reject(new Error(err.message || 'Failed to initiate Twitter authorization'));
      });
  });
};

/**
 * Post content to Twitter
 * 
 * @param options.text - Tweet text (max 280 characters for basic, 25000 for premium)
 * @param options.mediaBase64 - Optional base64-encoded media (image or video)
 * @param options.mediaType - Type of media ('image' or 'video')
 */
export const postToTwitter = async (options: {
  text?: string;
  mediaBase64?: string;
  mediaType?: string;
}): Promise<TwitterPostResult> => {
  const tokens = getTwitterTokens();
  
  if (!tokens?.accessToken || !tokens?.accessTokenSecret) {
    return {
      success: false,
      error: 'Not connected to Twitter. Please connect your account first.',
    };
  }

  try {
    const response = await fetch(TWITTER_POST_ENDPOINT, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        accessToken: tokens.accessToken,
        accessTokenSecret: tokens.accessTokenSecret,
        text: options.text,
        mediaBase64: options.mediaBase64,
        mediaType: options.mediaType,
      }),
    });

    const data = await response.json();

    if (!response.ok) {
      // Handle token expiry
      if (response.status === 401) {
        disconnectTwitter();
        return {
          success: false,
          error: 'Twitter session expired. Please reconnect your account.',
        };
      }

      return {
        success: false,
        error: data.error || 'Failed to post tweet',
      };
    }

    return {
      success: true,
      tweetId: data.tweetId,
      tweetUrl: data.tweetUrl,
    };
  } catch (err: any) {
    return {
      success: false,
      error: err.message || 'Failed to post tweet',
    };
  }
};

/**
 * Convert a blob URL to base64
 * Useful for preparing images/videos for upload
 */
export const blobUrlToBase64ForTwitter = async (blobUrl: string): Promise<string> => {
  const response = await fetch(blobUrl);
  const blob = await response.blob();
  
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => {
      // Remove the data URL prefix (data:image/png;base64,)
      const base64 = (reader.result as string).split(',')[1];
      resolve(base64);
    };
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
};

/**
 * Get MIME type from a data URL or blob
 */
export const getMimeType = (dataUrl: string): string => {
  if (dataUrl.startsWith('data:')) {
    const match = dataUrl.match(/data:([^;]+);/);
    return match ? match[1] : 'image/png';
  }
  // Default for unknown types
  return 'image/png';
};

/**
 * Prepare media for Twitter posting
 * Handles both data URLs and blob URLs
 */
export const prepareMediaForTwitter = async (
  mediaUrl: string
): Promise<{ base64: string; mimeType: string } | null> => {
  try {
    if (mediaUrl.startsWith('data:')) {
      // Extract base64 and mime type from data URL
      const mimeType = getMimeType(mediaUrl);
      const base64 = mediaUrl.split(',')[1];
      return { base64, mimeType };
    } else if (mediaUrl.startsWith('blob:')) {
      // Fetch blob and convert to base64
      const response = await fetch(mediaUrl);
      const blob = await response.blob();
      const mimeType = blob.type || 'image/png';
      
      const base64 = await new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => {
          const result = (reader.result as string).split(',')[1];
          resolve(result);
        };
        reader.onerror = reject;
        reader.readAsDataURL(blob);
      });
      
      return { base64, mimeType };
    } else if (mediaUrl.startsWith('http://') || mediaUrl.startsWith('https://')) {
      // Fetch remote URL and convert to base64
      const response = await fetch(mediaUrl);
      const blob = await response.blob();
      const mimeType = blob.type || 'image/png';
      
      const base64 = await new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => {
          const result = (reader.result as string).split(',')[1];
          resolve(result);
        };
        reader.onerror = reject;
        reader.readAsDataURL(blob);
      });
      
      return { base64, mimeType };
    }
    
    return null;
  } catch (err) {
    console.error('Failed to prepare media for Twitter:', err);
    return null;
  }
};
